// ============================================================================
// App routes.
// ----------------------------------------------------------------------------
//   /            public landing
//   /login       real login form (POST /api/auth/login)
//   /app         protected shell (RequireAuth) with the dashboard as index and
//                stubs for every app page so per-page agents can fill them next.
// The DASHBOARD is fully wired to real data now; the other /app pages render a
// Pulse placeholder until their agent builds them out.
//
// Per-page scaffolds wired here so the agents that own them never touch this
// router (they only replace their own component file's body):
//   /app/clients              -> ClientsPage         (pages/clients)
//   /app/clients/:id          -> ClientDetailPage    (pages/client-detail)
//   /app/clients/:id/report   -> ClientReportPage    (same component, per-client)
//   /app/sessions             -> SessionsPage        (pages/sessions)
//   /app/sessions/:id         -> SessionsPage        (session log/detail)
//   /app/exercises            -> ExercisesPage       (pages/exercises)
//   /app/templates            -> TemplatesPage       (pages/templates)
//   /app/reports/client       -> ClientReportPage    (pages/report-client)
//   /app/reports/business     -> BusinessReportPage  (pages/report-business)
//   /app/account              -> AccountPage         (pages/account)
//   /                         -> LandingPage         (app/LandingPage)
// ============================================================================
import { createBrowserRouter, Navigate } from "react-router-dom";
import { AppShell } from "./AppShell";
import { DashboardPage } from "./DashboardPage";
import { CalendarPage } from "./CalendarPage";
import { LoginPage } from "./LoginPage";
import { SignupPage } from "./SignupPage";
import { LandingPage } from "./LandingPage";
import { ForgotPasswordPage } from "./ForgotPasswordPage";
import { ResetPasswordPage } from "./ResetPasswordPage";
import { RequireAuth } from "../auth/RequireAuth";
// --- Client portal realm (separate auth, own AppShell) ---------------------
import { ClientPortalApp } from "../client/ClientPortalApp";
import { ClientAppShell } from "../client/ClientAppShell";
import { RequireClientAuth } from "../client/auth/RequireClientAuth";
import { ClientClaimPage } from "../client/pages/ClientClaimPage";
import { ClientLoginPage } from "../client/pages/ClientLoginPage";
import { ClientForgotPage } from "../client/pages/ClientForgotPage";
import { ClientResetPage } from "../client/pages/ClientResetPage";
import { ClientDashboardPage } from "../client/pages/ClientDashboardPage";
import { ClientLogPage } from "../client/pages/ClientLogPage";
import { ClientCalendarPage } from "../client/pages/ClientCalendarPage";
import { ClientMessagesPage } from "../client/pages/ClientMessagesPage";
import { ClientAccountPage } from "../client/pages/ClientAccountPage";
import { ClientReportPage as ClientPortalReportPage } from "../client/pages/ClientReportPage";
// --- PUBLIC, login-free QR check-in (what the check-in QR opens) ----------
import { PublicCheckinPage } from "../checkin/PublicCheckinPage";
// Per-page scaffolds (default exports owned by parallel agents — see each file).
import ClientsPage from "../pages/clients/ClientsPage";
import ClientDetailPage from "../pages/client-detail/ClientDetailPage";
import ClientReportPage from "../pages/report-client/ClientReportPage";
import SessionsPage from "../pages/sessions/SessionsPage";
import ExercisesPage from "../pages/exercises/ExercisesPage";
import TemplatesPage from "../pages/templates/TemplatesPage";
import BusinessReportPage from "../pages/report-business/BusinessReportPage";
import AccountPage from "../pages/account/AccountPage";
import ProfilePage from "../pages/account/ProfilePage";
import VerifyEmailPage from "../pages/account/VerifyEmailPage";

export const router = createBrowserRouter([
  { path: "/", element: <LandingPage /> },
  { path: "/login", element: <LoginPage /> },
  // Wave 7: the expanded trainer signup (create account → verify email → studio).
  { path: "/signup", element: <SignupPage /> },
  // Wave 8: PUBLIC self-service password recovery (trainer realm). /forgot
  // requests a reset link by email (non-enumerating); /account/reset?token= is
  // where the emailed link lands — the single-use token IS the auth, so this is
  // deliberately OUTSIDE RequireAuth (a locked-out trainer has no session). On a
  // successful reset the server signs the trainer in and the page enters /app.
  { path: "/forgot", element: <ForgotPasswordPage /> },
  { path: "/account/reset", element: <ResetPasswordPage /> },
  // PUBLIC, no-auth QR check-in: the URL minted by POST /sessions/{id}/checkin-qr
  // resolves here. The client scans the QR, sees the session, and confirms
  // arrival — no trainer or client login required (the token is the auth).
  { path: "/checkin/:token", element: <PublicCheckinPage /> },
  {
    element: <RequireAuth />,
    children: [
      // Wave 6: the trainer email-verification LANDING route. The verification
      // email links to {VF_PUBLIC_BASE_URL}/account/verify-email?token=… (a root
      // path, NOT under /app), so it's mounted here — authenticated (the confirm
      // endpoint is trainer-scoped) but OUTSIDE the AppShell so it reads as a
      // focused confirmation screen.
      { path: "/account/verify-email", element: <VerifyEmailPage /> },
      {
        path: "/app",
        element: <AppShell />,
        children: [
          // FULLY WIRED dashboard.
          { index: true, element: <DashboardPage /> },
          { path: "calendar", element: <CalendarPage /> },

          // --- per-page scaffolds (default-export, owned by parallel agents) ---
          // OWNED by the clients agent (pages/clients).
          { path: "clients", element: <ClientsPage /> },
          // OWNED by the client-detail agent (pages/client-detail).
          { path: "clients/:id", element: <ClientDetailPage /> },
          // OWNED by the client-report agent (pages/report-client). Reachable
          // both standalone and per-client.
          { path: "clients/:id/report", element: <ClientReportPage /> },
          // OWNED by the sessions agent (pages/sessions). Owns the list, the
          // schedule entry (?client=) and the session log/detail (:id).
          { path: "sessions", element: <SessionsPage /> },
          { path: "sessions/:id", element: <SessionsPage /> },
          // OWNED by the exercises agent (pages/exercises).
          { path: "exercises", element: <ExercisesPage /> },
          // OWNED by the templates agent (pages/templates).
          { path: "templates", element: <TemplatesPage /> },
          // OWNED by the client-report agent (pages/report-client).
          { path: "reports/client", element: <ClientReportPage /> },
          // OWNED by the business-report agent (pages/report-business).
          { path: "reports/business", element: <BusinessReportPage /> },
          // OWNED by the account agent (pages/account).
          { path: "account", element: <AccountPage /> },
          // Wave 7: the trainer's PUBLIC marketing profile editor.
          { path: "account/profile", element: <ProfilePage /> },

          { path: "*", element: <Navigate to="/app" replace /> },
        ],
      },
    ],
  },

  // --- CLIENT PORTAL realm (mobile-first installable PWA) ------------------
  // Entirely separate from the trainer /app tree above. The portal has its own
  // ClientAuthProvider (ClientPortalApp), its own AppShell + bottom nav, and its
  // own RequireClientAuth guard. A client claims via /client/claim?token=… (the
  // link the backend mints), logs in at /me/login, and lives under /me/*.
  {
    element: <ClientPortalApp />,
    children: [
      // Public portal routes (no client session required).
      { path: "/client/claim", element: <ClientClaimPage /> },
      { path: "/me/login", element: <ClientLoginPage /> },
      // Wave 8: PUBLIC self-service password recovery (client realm). /me/forgot
      // requests a reset link by email or portal name (non-enumerating);
      // /client/reset?token= is where the emailed link lands (mirrors the
      // existing /client/claim invite-link path — the token IS the auth).
      { path: "/me/forgot", element: <ClientForgotPage /> },
      { path: "/client/reset", element: <ClientResetPage /> },
      // Guarded portal routes (active client session required).
      {
        element: <RequireClientAuth />,
        children: [
          {
            path: "/me",
            element: <ClientAppShell />,
            children: [
              { index: true, element: <ClientDashboardPage /> },
              { path: "log", element: <ClientLogPage /> },
              { path: "calendar", element: <ClientCalendarPage /> },
              { path: "messages", element: <ClientMessagesPage /> },
              // Wave 8: the client's own settings (change password + sign out).
              { path: "account", element: <ClientAccountPage /> },
              { path: "report", element: <ClientPortalReportPage /> },
            ],
          },
        ],
      },
    ],
  },

  // Anything else outside the app -> landing.
  { path: "*", element: <Navigate to="/" replace /> },
]);
